import type { FunctionComponent } from 'react'
import React, { createContext, useContext } from 'react'
import type { ServiceIdentifier } from './Container'
import { Container } from './Container'
import { ComponentNotReady } from '@ztechapps/webui.util'

// A real `Container` (not `{}`) so any app that renders shared components
// (eg. `UpdateProfile`) without ever mounting a `DependencyProvider` — see
// `apps/asgard/gatekeeper-ui` — still resolves the cross-module fallback
// bindings `Container`'s constructor registers (`WebuiIocKey.company.companySelect`)
// instead of throwing `container.get is not a function`.
const DependencyContext = createContext<Container>(new Container())

export const DependencyProvider: FunctionComponent<{
  container: Container
  children: React.ReactNode
}> = ({ container, children }) => {
  return <DependencyContext.Provider value={container}>{children}</DependencyContext.Provider>
}

function useInjectCore<T>(key: ServiceIdentifier): T {
  const container = useContext(DependencyContext)
  if (!container) {
    throw new Error('useInject must be used within a DependencyProvider')
  }

  try {
    return container.get<T>(key)
  } catch (e) {
    console.error(e)
    throw e
  }
}

export function useInjectComponent<T>(key: ServiceIdentifier): FunctionComponent<T> {
  try {
    return useInjectCore<FunctionComponent<T>>(key)
  } catch (e) {
    return () => (
      <ComponentNotReady>
        <div className="small fst-italic my-1 ">{e.message}</div>
      </ComponentNotReady>
    )
  }
}

export function useInject<T>(key: ServiceIdentifier): T {
  try {
    return useInjectCore<T>(key)
  } catch (e) {
    console.error(e)
  }
}
